import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BarChart3, Bell, Check, Plus, Trash2, X } from 'lucide-react';
import {
  beendeUmfrage,
  createMitteilung,
  createUmfrage,
  fetchMitteilungen,
  fetchUmfragen,
  loescheUmfrage,
  ziehMitteilungZurueck,
  type Umfrage,
} from '@/api/anliegen.api';
import { fetchTarife } from '@/api/plans.api';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { zahl } from '@/components/format';

// ─── Abstimmungen und Mitteilungen ───────────────────────────────────────────
//
// Zwei Wege vom Betreiber zu den Kunden, die beide in der Anwendung enden und
// nicht im Postfach: eine Frage, die beantwortet werden kann, und ein Hinweis,
// der bestätigt werden muss.
//
// Zusammen auf einer Seite, weil sie sich denselben Empfängerkreis teilen und
// dieselbe Zurückhaltung verlangen: was jede Woche im Weg steht, wird
// weggeklickt, ohne gelesen zu werden – und dann taugt auch die
// Wartungsankündigung nichts mehr.

type Reiter = 'umfragen' | 'mitteilungen';

const ART_LABELS: Record<string, string> = {
  info: 'Hinweis',
  warnung: 'Warnung',
  wartung: 'Wartung',
};

const ART_FARBEN: Record<string, string> = {
  info: 'bg-blue-50 text-blue-700',
  warnung: 'bg-amber-50 text-amber-800',
  wartung: 'bg-violet-50 text-violet-700',
};

/** Ein Datum in `n` Tagen, als Wert für ein `<input type="date">`. */
function inTagen(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function MitwirkungPage() {
  const [reiter, setReiter] = useState<Reiter>('umfragen');

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <h1 className="text-base font-semibold text-slate-800 sm:text-lg">
          Abstimmungen &amp; Mitteilungen
        </h1>
      </header>

      <div className="shrink-0 border-b border-slate-200 bg-white px-4">
        <nav className="flex gap-1" aria-label="Bereich">
          {(
            [
              ['umfragen', 'Abstimmungen', BarChart3],
              ['mitteilungen', 'Mitteilungen', Bell],
            ] as const
          ).map(([wert, label, Icon]) => (
            <button
              key={wert}
              onClick={() => setReiter(wert)}
              className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium ${
                reiter === wert
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          {reiter === 'umfragen' ? <Abstimmungen /> : <Mitteilungen />}
        </div>
      </main>
    </div>
  );
}

// ─── Empfängerkreis ──────────────────────────────────────────────────────────
//
// Derselbe wie beim Rundschreiben – und dieselbe Feinheit: wer nach dem
// Vorgabe-Tarif filtert, erreicht auch die Konten ohne eigene Zuweisung. Das
// entscheidet der Server; hier steht nur die Auswahl.

function KreisFelder({
  planId,
  setPlanId,
  nurBesitzer,
  setNurBesitzer,
}: {
  planId: string;
  setPlanId: (v: string) => void;
  nurBesitzer: boolean;
  setNurBesitzer: (v: boolean) => void;
}) {
  const { data } = useQuery({ queryKey: ['admin', 'plans'], queryFn: fetchTarife });

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Empfänger</span>
        <select
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Alle Konten</option>
          {data?.data.map((t) => (
            <option key={t.id} value={t.id}>
              Nur Tarif „{t.name}"
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-end gap-2 pb-2">
        <input
          type="checkbox"
          checked={nurBesitzer}
          onChange={(e) => setNurBesitzer(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300"
        />
        <span className="text-sm text-slate-700">
          Nur Besitzer
          <span className="block text-xs text-slate-400">
            Konten, denen mindestens ein Stammbaum gehört
          </span>
        </span>
      </label>
    </div>
  );
}

// ─── Abstimmungen ────────────────────────────────────────────────────────────

function Abstimmungen() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [offen, setOffen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'umfragen'],
    queryFn: fetchUmfragen,
  });

  const aktualisieren = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'umfragen'] });

  const beenden = useMutation({
    mutationFn: beendeUmfrage,
    onSuccess: async () => {
      await aktualisieren();
      toast.success('Abstimmung beendet.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const loeschen = useMutation({
    mutationFn: loescheUmfrage,
    onSuccess: async () => {
      await aktualisieren();
      toast.success('Abstimmung gelöscht.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Eine Frage an Ihre Kunden. Die Antwort ist anonym – gespeichert wird nur, dass
          jemand abgestimmt hat, nicht wofür.
        </p>
        <button
          onClick={() => setOffen(true)}
          className="flex shrink-0 items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Abstimmung starten
        </button>
      </div>

      {offen && <UmfrageFormular onFertig={() => setOffen(false)} />}

      {isLoading && <p className="py-8 text-center text-sm text-slate-400">Wird geladen…</p>}

      {data?.length === 0 && !isLoading && (
        <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
          Noch keine Abstimmung.
        </p>
      )}

      {data?.map((u) => (
        <UmfrageKarte
          key={u.id}
          umfrage={u}
          onBeenden={() => beenden.mutate(u.id)}
          onLoeschen={async () => {
            const ok = await confirm({
              title: 'Abstimmung löschen',
              message: `„${u.frage}" wird mit ihrem Ergebnis gelöscht. Das lässt sich nicht rückgängig machen.`,
              confirmLabel: 'Löschen',
              variant: 'danger',
            });
            if (ok) loeschen.mutate(u.id);
          }}
        />
      ))}
    </div>
  );
}

function UmfrageKarte({
  umfrage,
  onBeenden,
  onLoeschen,
}: {
  umfrage: Umfrage;
  onBeenden: () => void;
  onLoeschen: () => void;
}) {
  const laeuft = umfrage.status === 'laeuft';
  const fuehrend = Math.max(...umfrage.optionen.map((o) => o.stimmen), 0);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{umfrage.frage}</p>
          {umfrage.text && <p className="mt-0.5 text-sm text-slate-500">{umfrage.text}</p>}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            laeuft ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {laeuft ? 'läuft' : 'beendet'}
        </span>
      </div>

      <ul className="mt-3 space-y-2">
        {umfrage.optionen.map((o) => {
          const anteil = umfrage.gesamt > 0 ? (o.stimmen / umfrage.gesamt) * 100 : 0;
          return (
            <li key={o.id}>
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-slate-700">{o.text}</span>
                <span className="shrink-0 tabular-nums text-slate-500">
                  {zahl(o.stimmen)}
                  <span className="ml-1 text-xs text-slate-400">
                    {umfrage.gesamt > 0 ? `${Math.round(anteil)} %` : ''}
                  </span>
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${
                    o.stimmen === fuehrend && fuehrend > 0 ? 'bg-blue-600' : 'bg-slate-300'
                  }`}
                  style={{ width: `${anteil}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <p className="text-xs text-slate-400">
          {zahl(umfrage.teilnehmer)} {umfrage.teilnehmer === 1 ? 'Teilnehmer' : 'Teilnehmer'} ·
          angelegt {datum(umfrage.createdAt)}
          {umfrage.erstelltVon && ` von ${umfrage.erstelltVon}`}
        </p>
        <div className="flex gap-2">
          {laeuft && (
            <button
              onClick={onBeenden}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Beenden
            </button>
          )}
          <button
            onClick={onLoeschen}
            className="flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

function UmfrageFormular({ onFertig }: { onFertig: () => void }) {
  const queryClient = useQueryClient();
  const [frage, setFrage] = useState('');
  const [text, setText] = useState('');
  const [optionen, setOptionen] = useState(['', '']);
  const [planId, setPlanId] = useState('');
  const [nurBesitzer, setNurBesitzer] = useState(false);

  const anlegen = useMutation({
    mutationFn: () =>
      createUmfrage({
        frage: frage.trim(),
        text: text.trim() || null,
        optionen: optionen.map((o) => o.trim()).filter(Boolean),
        planId: planId || null,
        nurBesitzer,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'umfragen'] });
      toast.success('Abstimmung gestartet.');
      onFertig();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const gefuellt = optionen.filter((o) => o.trim()).length;
  const kannSpeichern = frage.trim().length >= 5 && gefuellt >= 2;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (kannSpeichern) anlegen.mutate();
      }}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Frage</span>
        <input
          value={frage}
          onChange={(e) => setFrage(e.target.value)}
          placeholder="Welche Funktion sollen wir als Nächstes bauen?"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          Erläuterung <span className="font-normal text-slate-400">(optional)</span>
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>

      <div>
        <span className="mb-1 block text-sm font-medium text-slate-700">Antworten</span>
        <div className="space-y-2">
          {optionen.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={o}
                onChange={(e) =>
                  setOptionen((alt) => alt.map((x, j) => (j === i ? e.target.value : x)))
                }
                placeholder={`Antwort ${i + 1}`}
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              {optionen.length > 2 && (
                <button
                  type="button"
                  onClick={() => setOptionen((alt) => alt.filter((_, j) => j !== i))}
                  className="rounded-md px-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label={`Antwort ${i + 1} entfernen`}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
        {optionen.length < 10 && (
          <button
            type="button"
            onClick={() => setOptionen((alt) => [...alt, ''])}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
          >
            + Antwort hinzufügen
          </button>
        )}
      </div>

      <KreisFelder
        planId={planId}
        setPlanId={setPlanId}
        nurBesitzer={nurBesitzer}
        setNurBesitzer={setNurBesitzer}
      />

      {/* Der Hinweis steht vor dem Knopf, nicht danach: eine gestartete
          Abstimmung sammelt Stimmen, die sich nicht mehr einzeln zurücknehmen
          lassen – dafür ist sie anonym. */}
      <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Die Antworten sind anonym. Gespeichert wird, DASS ein Konto abgestimmt hat, nicht
        wofür – auch für Sie nicht einsehbar. Eine abgegebene Stimme lässt sich deshalb
        nicht zurücknehmen.
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onFertig}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={!kannSpeichern || anlegen.isPending}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Starten
        </button>
      </div>
    </form>
  );
}

// ─── Mitteilungen ────────────────────────────────────────────────────────────

function Mitteilungen() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [offen, setOffen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'mitteilungen'],
    queryFn: fetchMitteilungen,
  });

  const zurueckziehen = useMutation({
    mutationFn: ziehMitteilungZurueck,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'mitteilungen'] });
      toast.success('Mitteilung zurückgezogen.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Erscheint beim nächsten Laden der Seite und bleibt stehen, bis sie bestätigt wird.
        </p>
        <button
          onClick={() => setOffen(true)}
          className="flex shrink-0 items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Mitteilung verfassen
        </button>
      </div>

      {offen && <MitteilungFormular onFertig={() => setOffen(false)} />}

      {isLoading && <p className="py-8 text-center text-sm text-slate-400">Wird geladen…</p>}

      {data?.length === 0 && !isLoading && (
        <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
          Noch keine Mitteilung.
        </p>
      )}

      {data?.map((m) => (
        <div key={m.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium text-slate-900">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    ART_FARBEN[m.art] ?? ART_FARBEN.info
                  }`}
                >
                  {ART_LABELS[m.art] ?? m.art}
                </span>
                {m.titel}
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{m.text}</p>
            </div>
            {!m.aktiv ? (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                zurückgezogen
              </span>
            ) : (
              m.sichtbarBis &&
              new Date(m.sichtbarBis) < new Date() && (
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  abgelaufen
                </span>
              )
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-400">
              {zahl(m.bestaetigt)} mal bestätigt · {datum(m.createdAt)}
              {m.erstelltVon && ` von ${m.erstelltVon}`}
              {/* Abgelaufen ist etwas anderes als zurückgezogen: niemand hat
                  eingegriffen, die Frist war um. Beides sieht man der Liste
                  sonst nicht an. */}
              {m.sichtbarBis &&
                (new Date(m.sichtbarBis) < new Date()
                  ? ` · abgelaufen am ${datum(m.sichtbarBis)}`
                  : ` · läuft ab am ${datum(m.sichtbarBis)}`)}
            </p>
            {m.aktiv && !(m.sichtbarBis && new Date(m.sichtbarBis) < new Date()) && (
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Mitteilung zurückziehen',
                    message: `„${m.titel}" erscheint danach niemandem mehr. Sie bleibt als Nachweis stehen.`,
                    confirmLabel: 'Zurückziehen',
                  });
                  if (ok) zurueckziehen.mutate(m.id);
                }}
                className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Zurückziehen
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function MitteilungFormular({ onFertig }: { onFertig: () => void }) {
  const queryClient = useQueryClient();
  const [titel, setTitel] = useState('');
  const [text, setText] = useState('');
  const [art, setArt] = useState('info');
  const [planId, setPlanId] = useState('');
  const [nurBesitzer, setNurBesitzer] = useState(false);
  // Vorbelegt auf zwei Wochen. Die häufigste Mitteilung ist eine
  // Wartungsankündigung, und die ist nach dem Termin wertlos – wer kein Ende
  // setzt, lässt sie stehen, bis jemand sie von Hand zurückzieht.
  const [laeuftAb, setLaeuftAb] = useState(true);
  const [bis, setBis] = useState(() => inTagen(14));

  const anlegen = useMutation({
    mutationFn: () =>
      createMitteilung({
        titel: titel.trim(),
        text: text.trim(),
        art,
        planId: planId || null,
        nurBesitzer,
        // Ende des gewählten Tages: wer den 10. einträgt, meint den ganzen
        // 10., nicht dessen ersten Augenblick.
        sichtbarBis: laeuftAb && bis ? new Date(`${bis}T23:59:59`).toISOString() : null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'mitteilungen'] });
      toast.success('Mitteilung veröffentlicht.');
      onFertig();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const kannSpeichern = titel.trim().length >= 3 && text.trim().length >= 10;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (kannSpeichern) anlegen.mutate();
      }}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Titel</span>
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            placeholder="Wartung am Sonntag"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Art</span>
          <select
            value={art}
            onChange={(e) => setArt(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="info">Hinweis</option>
            <option value="warnung">Warnung</option>
            <option value="wartung">Wartung</option>
          </select>
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Text</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Am Sonntag von 2 bis 4 Uhr ist die Anwendung nicht erreichbar."
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>

      <KreisFelder
        planId={planId}
        setPlanId={setPlanId}
        nurBesitzer={nurBesitzer}
        setNurBesitzer={setNurBesitzer}
      />

      {/* Ein Ende ist der Normalfall, kein Sonderfall – deshalb angehakt. */}
      <div className="rounded-md border border-slate-200 p-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={laeuftAb}
            onChange={(e) => setLaeuftAb(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          <span className="text-sm font-medium text-slate-700">Läuft ab am</span>
        </label>

        {laeuftAb ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={bis}
              min={inTagen(0)}
              onChange={(e) => setBis(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            {[7, 14, 30].map((tage) => (
              <button
                key={tage}
                type="button"
                onClick={() => setBis(inTagen(tage))}
                className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
              >
                {tage} Tage
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-1.5 text-xs text-slate-500">
            Bleibt stehen, bis Sie sie zurückziehen. Sinnvoll nur, wenn der Hinweis
            dauerhaft gilt.
          </p>
        )}
      </div>

      <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Sparsam einsetzen: was jede Woche im Weg steht, wird weggeklickt, ohne gelesen zu
        werden – und dann taugt auch die nächste Wartungsankündigung nichts mehr.
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onFertig}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={!kannSpeichern || anlegen.isPending}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Veröffentlichen
        </button>
      </div>
    </form>
  );
}
